import { forwardRef, useEffect, useMemo } from "react";
import { Form, Radio, Row, Col, FormInstance } from "antd";
import Indicator from "@/components/shared/Indicator";
import { TMSScanOrderConfig, TMSShippingLabelMode } from "@/types";

const DefaultScanConfig: TMSScanOrderConfig = {
  hasCartonLabel: false,
  isSSCCOrder: false,
  shippingLabelMode: TMSShippingLabelMode.Disabled,
  applyPalletLabel: true,
  requirePalletDimension: false,
};

interface ScanConfigProps {
  className?: string;
  onFieldsChange: (values: TMSScanOrderConfig) => void;
}

const SharedOptions = [
  { label: "No", value: false },
  { label: "Yes", value: true },
];

const ShippingLabelOptions = [
  { label: "Disabled", value: TMSShippingLabelMode.Disabled },
  { label: "Pallet", value: TMSShippingLabelMode.Pallet },
  { label: "Carton", value: TMSShippingLabelMode.Carton },
];

const ScanConfig = forwardRef<
  FormInstance<TMSScanOrderConfig>,
  ScanConfigProps
>((props, ref) => {
  const { className, onFieldsChange } = props;
  const [form] = Form.useForm<TMSScanOrderConfig>();

  const hasCartonLabel = Form.useWatch("hasCartonLabel", form);
  const shippingLabelMode = Form.useWatch("shippingLabelMode", form);

  const shippingLabelOptions = useMemo(() => {
    const items = [...ShippingLabelOptions] as {
      label: string;
      value: string;
      disabled?: boolean;
    }[];
    if (!hasCartonLabel) {
      items[2].disabled = true;
    } else {
      items[2].disabled = false;
    }
    return items;
  }, [hasCartonLabel]);

  useEffect(() => {
    if (
      shippingLabelMode === TMSShippingLabelMode.Carton &&
      hasCartonLabel === false
    ) {
      form.setFieldsValue({ shippingLabelMode: TMSShippingLabelMode.Disabled });
    }
  }, [shippingLabelMode, hasCartonLabel, form]);

  return (
    <div className={className}>
      <Indicator label="Scan Info" />
      <div className="border-[1px] rounded-md p-4 pb-0 bg-gray-100/50 border-gray-200 bg-white">
        <Form
          form={form}
          layout="vertical"
          initialValues={DefaultScanConfig}
          ref={ref}
          onValuesChange={(_, all) => onFieldsChange(all)}
        >
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                label="Has carton label?"
                name="hasCartonLabel"
                rules={[{ required: true, message: "Please choose." }]}
              >
                <Radio.Group
                  options={SharedOptions}
                  optionType="button"
                  buttonStyle="solid"
                />
              </Form.Item>
            </Col>

            <Col span={8}>
              <Form.Item
                label="SSCC order?"
                name="isSSCCOrder"
                rules={[{ required: true, message: "Please choose." }]}
              >
                <Radio.Group
                  options={SharedOptions}
                  optionType="button"
                  buttonStyle="solid"
                />
              </Form.Item>
            </Col>

            <Col span={8}>
              <Form.Item
                label="Apply Pallet Label?"
                name="applyPalletLabel"
                rules={[{ required: true, message: "Please choose." }]}
              >
                <Radio.Group
                  options={SharedOptions}
                  optionType="button"
                  buttonStyle="solid"
                />
              </Form.Item>
            </Col>

            <Col span={8}>
              <Form.Item
                label="Required Pallet Dimension?"
                name="requirePalletDimension"
                rules={[{ required: true, message: "Please choose." }]}
              >
                <Radio.Group
                  options={SharedOptions}
                  optionType="button"
                  buttonStyle="solid"
                />
              </Form.Item>
            </Col>

            <Col span={8}>
              <Form.Item
                label="Shipping label mode"
                name="shippingLabelMode"
                rules={[{ required: true, message: "Please choose." }]}
              >
                <Radio.Group
                  options={shippingLabelOptions}
                  optionType="button"
                  buttonStyle="solid"
                />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </div>
    </div>
  );
});

export default ScanConfig;
